import { getTimeZone } from 'util/time';
import { VcsEgressExternalControllerV3Service } from 'api';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import { showSnackbar, useSnackbar } from '@web-chat-core/ui-kit';
import { RecordingMetadataBody } from 'api/types';
import { recordingStore } from 'state/store';
import { RECORDING_STATUS } from 'state/store/recording/types';
import {
    selectIsMeRecordingInitiator,
    selectRecordingData,
    selectRecordingInitiatorData,
    selectRecordingStatus,
    selectRoomId,
} from 'state/selectors';

interface UseRecordingResult {
    startRecording(): void;
    stopRecording(): void;
    updateRecordingData(metadata: RecordingMetadataBody | null): void;
}

export const showRecordingSnackbar = (): void => {
    if (selectRecordingStatus() === RECORDING_STATUS.RECORDING) {
        showSnackbar(t('web_call_record_video_snackbar'));
    }
};

export const useRecording = (): UseRecordingResult => {
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbar();

    const startRecording = async () => {
        recordingStore.setWaitingForStartStatus();

        const roomId = selectRoomId();
        const timeZone = getTimeZone();

        const { error, payload } = await VcsEgressExternalControllerV3Service.startVideoEgress(-1, {
            roomId,
            timeZone,
        });

        if (payload) {
            console.log('Запись начнётся при получении метаданный комнаты о старте записи');
        }

        if (error) {
            if (error.hasError('VCS_EGRESS_OPERATION_EXCEED_LIMIT')) {
                showSnackbar(t('web_call_record_video_snackbar_error_unavailable'), {
                    type: 'error',
                    testId: 'snackbar-record-unavailable-error',
                });
            } else {
                showSnackbar(t('web_call_record_start_video_snackbar_error'), {
                    type: 'error',
                    testId: 'snackbar-record-error',
                });
            }

            recordingStore.setInitialStatus();
        }
    };

    const stopRecording = async () => {
        const roomId = selectRoomId();

        const { error, payload } = await VcsEgressExternalControllerV3Service.stopVideoEgress(-1, {
            roomId,
        });

        if (payload) {
            console.log('Попытка остановить запись');
        }
        if (error) {
            showSnackbar(t('web_call_record_stop_video_snackbar_error'), {
                type: 'error',
                testId: 'snackbar-stop-video-error',
            });
        }
    };

    const updateRecordingData = (metadata: RecordingMetadataBody | null) => {
        const prevRecordingMetadata = selectRecordingData();
        // Данные не изменились, ничего не делаем
        if (compareIsRecordingMetadataEqual(metadata, prevRecordingMetadata)) return;

        const isInitialRecordingState =
            !metadata || (metadata.coolDownRecording === null && metadata.initiatorId === null);

        if (isInitialRecordingState) {
            const recordingStatus = selectRecordingStatus();
            const isPreviousStateRecording =
                recordingStatus === RECORDING_STATUS.RECORDING || recordingStatus === RECORDING_STATUS.STOPPING;
            if (isPreviousStateRecording) {
                showSnackbar(t('web_call_record_stop_video_snackbar'), { testId: 'snackbar-stop-video' });
            }

            recordingStore.clear();
            recordingStore.setInitialStatus();
            return;
        }

        recordingStore.setRecordingData(metadata);

        // Проверяем только статусы записи
        if (metadata) {
            if (metadata.coolDownRecording) {
                recordingStore.setStoppingRecordingStatus();
                return;
            }

            recordingStore.setRecordingStatus();

            const isMeRecordingInitiator = selectIsMeRecordingInitiator();
            if (isMeRecordingInitiator) {
                showSnackbar(t('web_call_record_start_video_snackbar_you'));
                return;
            }

            const recordingInitiator = selectRecordingInitiatorData();
            const recordInitiator = recordingInitiator?.initiatorName;
            showSnackbar(t('web_call_record_start_video_snackbar', { 0: recordInitiator }));
        }
    };

    const compareIsRecordingMetadataEqual = (
        metadata1: RecordingMetadataBody | null,
        metadata2: RecordingMetadataBody | null,
    ) => {
        if (metadata1 === null && metadata2 === null) return true;

        if (metadata1 !== null && metadata2 !== null) {
            return (
                metadata1.coolDownRecording === metadata2.coolDownRecording &&
                metadata1.initiatorId === metadata2.initiatorId
            );
        }

        return false;
    };

    return {
        startRecording,
        stopRecording,
        updateRecordingData,
    };
};
